/**
 * Error summary (intake/spec §6, WCAG 3.3.1/3.3.3). On failed submit, a `role="alert"` box lists
 * each problem as a link that moves focus to the offending control. Auto-focuses itself on mount
 * so screen-reader + keyboard users land on the list of problems.
 */
import { useEffect, useRef } from "react";
import { AlertIcon } from "./icons.js";

export interface FieldErrorRef {
  /** id of the control to focus (or the group container). */
  targetId: string;
  message: string;
}

export function ErrorSummary({ errors }: { errors: FieldErrorRef[] }): JSX.Element | null {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (errors.length) ref.current?.focus();
  }, [errors]);

  if (!errors.length) return null;

  const focusTarget = (id: string) => {
    const el = document.getElementById(id) as HTMLElement | null;
    if (!el) return;
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    // radiogroups/containers get a tabindex so focus can land.
    if (!el.hasAttribute("tabindex") && !isNativelyFocusable(el)) {
      el.setAttribute("tabindex", "-1");
    }
    el.focus({ preventScroll: true });
  };

  return (
    <div
      className="td-error-summary"
      role="alert"
      tabIndex={-1}
      ref={ref}
      aria-labelledby="td-error-summary-title"
    >
      <p className="td-error-summary__title" id="td-error-summary-title">
        <AlertIcon width={18} height={18} />
        <span>
          There {errors.length === 1 ? "is 1 problem" : `are ${errors.length} problems`} with your
          report
        </span>
      </p>
      <ul className="td-error-summary__list">
        {errors.map((e) => (
          <li key={e.targetId}>
            <a
              href={`#${e.targetId}`}
              onClick={(ev) => {
                ev.preventDefault();
                focusTarget(e.targetId);
              }}
            >
              {e.message}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

function isNativelyFocusable(el: HTMLElement): boolean {
  return ["INPUT", "TEXTAREA", "SELECT", "BUTTON", "A"].includes(el.tagName);
}
